import { Children, useState } from 'react';
import type { JSX, ReactElement, ReactNode } from 'react';
import { Box } from '@mui/material';
import { tss } from 'tss-react/mui';
import { darkModeBackgroundImageElevation } from '@/utils/darkModeBackgroundImageElevation.ts';
import { getThemeColor } from '../../muiTheme';
import { TabsContext } from './TabsContext.ts';
import { useActiveTab, useActiveTabIdx } from './useActiveTab.ts';

export function Tabs({
    activeIdx = 0,
    children,
    className,
}: {
    activeIdx?: number;
    children: ReactNode;
    className?: string;
}) {
    return (
        <TabsContext.Provider value={useState(activeIdx)}>
            <div role="tablist" className={className}>
                {children}
            </div>
        </TabsContext.Provider>
    );
}

type CommonProps = {
    idx: number;
    children: ReactNode;
    className?: string;
};

export function RootTab({ idx, children, className }: CommonProps) {
    const [activeTab, setActiveTab] = useActiveTab();
    const active = idx === activeTab;
    return active ? (
        <span role="tab" aria-selected={active} className={className}>
            {children}
        </span>
    ) : (
        <Box
            component="button"
            role="tab"
            aria-selected={active}
            className={className}
            sx={{ cursor: active ? 'auto' : 'pointer' }}
            onClick={() => {
                setActiveTab(idx);
            }}
        >
            {children}
        </Box>
    );
}

export function TabPanel({
    idx,
    children,
    ...rootProps
}: Partial<CommonProps> & JSX.IntrinsicElements['div']) {
    const activeTab = useActiveTab()[0];
    return activeTab === idx ? <div {...rootProps}>{children}</div> : null;
}

const TabStatus = {
    default: 'default',
    active: 'active',
};

function getTabStatus(idx: number, activeTabIdx: number) {
    return activeTabIdx === idx ? TabStatus.active : TabStatus.default;
}

export function Tab({
    idx = 0,
    heading,
    subheading,
}: {
    idx?: number;
    heading: ReactNode;
    subheading: ReactNode;
}) {
    const activeTabIdx = useActiveTabIdx();
    const tabStatus = getTabStatus(idx, activeTabIdx);
    const { classes, cx } = useTabStyles();
    return (
        <RootTab
            idx={idx}
            className={cx(classes.tab, classes[`tab_${tabStatus}`])}
        >
            <span
                className={cx(classes.heading, classes[`heading_${tabStatus}`])}
            >
                {heading}
            </span>
            <span className={cx(classes.subheading)}>{subheading}</span>
        </RootTab>
    );
}

const useTabStyles = tss.create(({ theme }) => {
    const getColor = getThemeColor(theme);
    return {
        tab: {
            padding: 11,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            border: 'none',
            outline: 0,
            borderRadius: 4,
            textAlign: 'center',
            transition:
                'background-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        },
        [`tab_${TabStatus.active}`]: {
            boxShadow:
                '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
            background: getColor('primary'),
            border: 'none',
        },
        [`tab_${TabStatus.default}`]: {
            border:
                theme.palette.mode === 'dark'
                    ? theme.bino.color.boxShadowBorder
                    : `1px solid ${getColor('lightGray')}`,
            background: theme.palette.background.paper,
            backgroundImage: darkModeBackgroundImageElevation(theme, 1),

            '&:hover': {
                backgroundImage: darkModeBackgroundImageElevation(theme, 2),
                boxShadow:
                    '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
            },
        },
        heading: {
            fontWeight: 'bold',
            fontSize: 19,
            lineHeight: '28px',
        },
        [`heading_${TabStatus.active}`]: {
            color: getColor('white'),
        },
        [`heading_${TabStatus.default}`]: {
            color: theme.palette.primary.main,
        },
        subheading: {
            fontSize: 14,
            lineHeight: '22px',
            color: getColor('palePurple'),
        },
    };
});

export function TabGroup({
    children,
}: {
    readonly children:
        | ReactElement<typeof Tab>
        | ReadonlyArray<ReactElement<typeof Tab>>;
}) {
    return (
        <Box
            component="header"
            sx={(theme) => ({
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gridGap: theme.spacing(2),
                padding: 2,
                background: theme.palette.background.default,
            })}
        >
            {Children.map(children, (child, idx) => {
                return child && <child.type idx={idx} {...child.props} />;
            })}
        </Box>
    );
}
